import React, { useEffect, useState } from 'react'

import { Link, useNavigate, useParams } from 'react-router-dom'

import { User } from 'hooks/useUsers'
import { ProductSearchResult } from 'models/SearchResult'
import { PageLoadSpinner } from 'components/spinners/PageLoadSpinner'
import { useProductForm } from 'hooks/useProductForm'
import { ProductForm } from 'components/forms/ProductForm'
import { useDesignDocumentsForm } from 'hooks/useDesignDocumentsForm'
import { usePipeOpenings } from 'hooks/usePipeOpenings'
import { usePipeMaterials } from 'hooks/usePipeMaterials'
import { useProductTypes } from 'hooks/useProductTypes'
import { isMerchant } from 'models/User'
import { useFlowControlDevices } from 'hooks/useFlowControlDevices'
import { isCreated, isRestored, IssueNumberTag, StatusTag } from 'pages/manage/products/VersionHistoryModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightLong, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { Comment } from 'pages/manage/products/Comment'
import { diffBetweenProductSearchResults, WhatsChanged } from 'pages/manage/products/WhatsChanged'
import config from 'config'
import { isAddNewProductLine, isUpissueProduct } from 'utils/ActiveAction'
import { isApproved, isArchived, isEditsRequested, isPendingReview, isReadyForApproval } from 'utils/Metadata'
import { useAuth0Authentication } from 'hooks/useAuth0Authentication'


const getProductVersionApi = (productCode: string, version: number, accessToken: string): Promise<Response> => {
    return fetch(
    `${config.apiUrl}/products/get/${productCode}/versions/${version}`
        , {
        method: 'GET',
        redirect: 'follow',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": 'application/json'
        },
        }); 
  }

const ViewProductVersionScreen = ({ loggedInUser }:{ loggedInUser: User}) => {
        
    const { productCode } = useParams()
    const { version: versionParam } = useParams()
    const version: number = parseInt(versionParam) // TODO - add validation

    const navigate = useNavigate()
    const { getAuth0AccessToken } = useAuth0Authentication()

    const [previousProductVersion, setPreviousProductVersion] = useState<ProductSearchResult>(null)
    const [productVersion, setProductVersion] = useState<ProductSearchResult>(null)
    const [isVersionDetailsCollapsed, setIsVersionDetailsCollapsed] = useState<boolean>(false)

    const productForm = useProductForm() 
    
    const { 
        loadFormState: loadProductFormState,
    } = productForm

    const designDocumentsForm = useDesignDocumentsForm() 
    const { 
        loadFormState: loadDesignDocumentsFormState,
     } = designDocumentsForm

    const { pipeOpenings } = usePipeOpenings()
    const { pipeMaterials } = usePipeMaterials()
    const { productTypes } = useProductTypes()
    const { flowControlDevices } = useFlowControlDevices()


    const initialiseProductVersion = (pv: ProductSearchResult) => {
        
        setProductVersion(pv)
        loadProductFormState({
            ...pv,
        })
        loadDesignDocumentsFormState(pv.designDocuments)
    }

    useEffect(() => {
        const get = async (id: string, version: number) => {

            const accessToken: string = await getAuth0AccessToken()
            const response = await getProductVersionApi(id, version, accessToken)

            if (!response.ok) {
                navigate(`/`)
            }
            const getResponse: ProductSearchResult = await response.json()
            initialiseProductVersion(getResponse)
        }

        get(productCode, version)
    }, [productCode, version])

    useEffect(() => {
        const get = async (id: string, version: number) => {

            const accessToken: string = await getAuth0AccessToken()
            const response = await getProductVersionApi(id, version, accessToken)

            const getResponse: ProductSearchResult = await response.json()
            if (response.ok) {
                setPreviousProductVersion(getResponse)
            }
        }

        if (version > 0) {
            get(productCode, version - 1)
        }
    }, [productCode, version])

    const renderStatus = (previousProductVersion: ProductSearchResult, currentProductVersion: ProductSearchResult) => {

        const display = (version: ProductSearchResult, className: string = '') => {
            if (isCreated(version)) {
                return (<span className={`tag is-primary is-medium ${className}`}>{`Created`}</span>)
            } else {
                return (<StatusTag metadata={version?.metadata} className={`is-medium ${className}`} />)
            }
        }
   
        if (previousProductVersion && currentProductVersion && !isCreated(currentProductVersion)) {

            return (
                <div className="is-vcentered">
                    {display(previousProductVersion, `has-text-strikethrough`)} 
                    <FontAwesomeIcon className={`pl-2 pr-2`} size='1x' icon={faArrowRightLong} />
                    {display(productVersion)} 
                </div>
            )

        } else {
            return (display(productVersion))
        }
    }

    if (!productVersion || !productTypes || !pipeOpenings) return(
        <div className="container is-fullheight">
            <div className="columns mt-6 pt-6">
                <div className="column is-offset-one-quarter is-half" style={{padding: '5rem', borderRadius: 6}}>
                    <PageLoadSpinner />
                </div>
            </div>
        </div>
    )

    return (
        <div className="container is-fullheight">

            <div className="columns mt-6 pt-6">
                <div className="column is-offset-one-quarter is-half has-background-white p-0" style={{borderRadius: 6}}>
    
                    <div className="block pt-6 pr-6 pl-6 p-2 mb-0">
                        <div className="columns p-2" style={{ border: '1px solid rgb(228, 228, 228)', borderRadius: 10 }}>
                            <div className="column">
                                <div className="columns mb-0 is-vcentered">
                                    <div className="column is-8">
                                        {renderStatus(previousProductVersion, productVersion)}
                                    </div>
                                    <div className="column is-3">
                                        {productVersion?.metadata.lastUpissued ?
                                            <IssueNumberTag metadataInfo={productVersion?.metadata.lastUpissued} issueNumber={productVersion?.metadata.issueNumber} actionText={`Last issued by `} />                         
                                        : productVersion?.metadata?.created ?
                                            <IssueNumberTag metadataInfo={productVersion?.metadata?.created} issueNumber={productVersion?.metadata.issueNumber} actionText={`Created by `} />
                                        : null
                                        }
                                    </div>
                                    <div className="column is-1">
                                        <FontAwesomeIcon className="is-clickable id-medium is-pulled-right" onClick={() => setIsVersionDetailsCollapsed(!isVersionDetailsCollapsed)} icon={isVersionDetailsCollapsed ? faChevronDown : faChevronUp} />     
                                    </div>
                                </div>

                                {!isVersionDetailsCollapsed
                                ?
                                <div className="block">
                                
                                    <hr className="navbar-divider mt-0 mb-4" />

                                    <>
                                        {/* /New upissue comment */}
                                        {isUpissueProduct(productVersion?.metadata?.activeAction) &&
                                        productVersion?.metadata.lastEdited &&
                                        productVersion.metadata?.activeAction?.initiatedOnVersion === productVersion.metadata.version - 1 &&

                                            <Comment 
                                                messageStyle={`is-danger`}
                                                commenterName={productVersion?.metadata.lastEdited.byFullName}
                                                loggedInUser={loggedInUser}
                                                actionText={`upissued a product for review`}
                                                timestamp={productVersion?.metadata.lastEdited.timestamp}
                                                comment={``} 
                                                ComponentPulledRight={
                                                <span className="tag is-danger is-small is-pulled-right">Upissued</span>
                                                } />

                                        }

                                        {/* /Newly created product comment */}
                                        {isCreated(productVersion) &&
                                            (productVersion?.metadata.lastEdited && !productVersion?.metadata.lastModerated) &&

                                            <Comment 
                                                messageStyle={`is-success`}
                                                commenterName={productVersion?.metadata.lastEdited.byFullName}
                                                loggedInUser={loggedInUser}
                                                actionText={isAddNewProductLine(productVersion?.metadata?.activeAction) ? `added a new product line for review` : `added a new product for review`}
                                                timestamp={productVersion?.metadata.lastEdited.timestamp}
                                                comment={``} 
                                                ComponentPulledRight={
                                                <span className="tag is-success is-small is-pulled-right">Created</span>
                                                } />

                                        }

                                        {/* Requested edits */}
                                        {isEditsRequested(productVersion?.metadata) && productVersion?.metadata.lastModerated &&
                                            
                                            <Comment 
                                                messageStyle={`is-dark`}
                                                commenterName={productVersion?.metadata.lastModerated.byFullName}
                                                loggedInUser={loggedInUser}
                                                actionText={`requested edits`}
                                                timestamp={productVersion?.metadata.lastModerated.timestamp}
                                                comment={productVersion?.metadata.lastModerated.comment} 
                                                ComponentPulledRight={
                                                <span className="tag is-warning is-small is-pulled-right">Edits Requested</span>
                                                }
                                                />

                                        }

                                        {/* has made edits */}
                                        {isPendingReview(productVersion?.metadata) && productVersion?.metadata.lastEdited &&
                                        !isCreated(productVersion) && 
                                        !(isUpissueProduct(productVersion.metadata?.activeAction) && productVersion?.metadata?.activeAction?.initiatedOnVersion === productVersion.metadata?.version -1) &&
 
                                        <Comment 
                                            messageStyle={`is-info`}
                                            commenterName={productVersion?.metadata.lastEdited.byFullName}
                                            loggedInUser={loggedInUser}
                                            actionText={`has made edits`}
                                            timestamp={productVersion?.metadata.lastEdited.timestamp}
                                            comment={productVersion?.metadata.lastEdited.comment}
                                            ComponentPulledRight={
                                                <span className="tag is-link is-small is-pulled-right">Pending Review</span>
                                            } />

                                        }

                                        {/* has approved changes */}
                                        {isApproved(productVersion?.metadata) && 
                                        productVersion?.metadata.lastModerated &&
                                        !isRestored(productVersion, previousProductVersion) &&

                                        <Comment 
                                            messageStyle={`is-primary`}
                                            commenterName={productVersion?.metadata.lastModerated.byFullName}
                                            loggedInUser={loggedInUser}
                                            actionText={`has approved this product`}
                                            timestamp={productVersion?.metadata.lastModerated.timestamp}
                                            comment={productVersion?.metadata.lastModerated.comment}
                                            ComponentPulledRight={
                                                <span className="tag is-primary is-small is-pulled-right">Approved</span>
                                            } />

                                        }


                                        { (isArchived(productVersion?.metadata) && productVersion?.metadata.lastArchived) &&
                                            <div className="columns">
                                                <div className="column">
                                                <Comment 
                                                    messageStyle={`is-danger`}
                                                    commenterName={productVersion?.metadata.lastArchived.byFullName}
                                                    loggedInUser={loggedInUser}
                                                    actionText={`archived this product`}
                                                    timestamp={productVersion?.metadata.lastArchived.timestamp}
                                                    comment={``} 
                                                    ComponentPulledRight={
                                                    <span className="tag is-danger is-small is-pulled-right">Archived</span>
                                                    }
                                                    />
                                                </div>
                                            </div>
                                        }

                                        { isRestored(productVersion, previousProductVersion) && productVersion?.metadata.lastRestored &&
                                            <div className="columns">
                                                <div className="column">
                                                <Comment 
                                                    messageStyle={`is-primary`}
                                                    commenterName={productVersion?.metadata.lastRestored.byFullName}
                                                    loggedInUser={loggedInUser}
                                                    actionText={`restored this product`}
                                                    timestamp={productVersion?.metadata.lastRestored.timestamp}
                                                    comment={``} 
                                                    ComponentPulledRight={
                                                    <span className="tag is-primary is-small is-pulled-right">Restored</span>
                                                    }
                                                    />
                                                </div>
                                            </div>
                                        }

                                        {isReadyForApproval(productVersion.metadata) &&
                                          productVersion?.metadata.lastModerated &&
                                          <div className="columns">
                                            <div className="column">
                                                <Comment 
                                                    messageStyle={`is-primary`}
                                                    commenterName={productVersion?.metadata.lastModerated.byFullName}
                                                    loggedInUser={loggedInUser}
                                                    actionText={`marked as ready for approval`}
                                                    timestamp={productVersion?.metadata.lastModerated.timestamp}
                                                    comment={``} 
                                                    ComponentPulledRight={
                                                        <span className="tag is-primary is-small is-pulled-right">Ready for Approval</span>
                                                    }
                                                />
                                            </div>
                                        </div>
                                        }

                                    </>

                                    {!isApproved(productVersion?.metadata) &&
                                        <hr className="navbar-divider mt-0 mb-4" />
                                    }
                                        
                                    {productVersion && 
                                     previousProductVersion &&
                                     !isApproved(productVersion?.metadata) && 
                                     !isCreated(productVersion) && productVersion?.metadata?.version !== 1 &&
                                     !isEditsRequested(productVersion?.metadata) &&
                                     !isArchived(productVersion?.metadata) &&
                                     !isReadyForApproval(productVersion?.metadata)
                                     &&
                                        <>
                                            <WhatsChanged productDiff={diffBetweenProductSearchResults(productVersion, previousProductVersion)} />
                                            <hr className="navbar-divider mt-0 mb-4" />
                                        </>
                                    }

                                    <nav className="pagination is-small is-centered" role="navigation" aria-label="pagination">
                                        <Link to={`/view/product/${productCode}/versions/${version + 1}`} className={`pagination-next`}>Next</Link> {/* Add this versino doesn't exist state */}
                                        <Link onClick={(e)=> version === 0 && e.preventDefault()} to={`/view/product/${productCode}/versions/${version - 1}`} className={`pagination-previous ${version === 0 ? `is-disabled` : ``}`}>Previous</Link>
                                    </nav>

                            </div>
                            :
                            null
                            }

                            </div>
                        </div>
                    </div>

                    <div className="block pt-5 pr-6 pl-6 p-2 mb-0">
                        <h1 className="title is-3">{`View Product`}</h1>
                    </div> 

                    <ProductForm
                        isMerchant={isMerchant(loggedInUser)}
                        formView={'view'}
                        productForm={productForm}
                        designDocumentsForm={designDocumentsForm}
                        productTypes={productTypes}
                        pipeOpenings={pipeOpenings}
                        pipeMaterials={pipeMaterials}
                        flowControlDevices={flowControlDevices} />

                </div>                     
            </div>
        </div>
    )
}

export { ViewProductVersionScreen }